// frontend/src/pages/EmailAccountsPage.tsx - FIXED VERSION
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// Import hooks
import {
  useApi,
  useMutation
} from '@/hooks/useApi';

// Import API types
import type {
  EmailAccount,
  EmailAccountsResponse,
  OAuth2AuthorizeResponse,
  UserRole
} from '@/api/types';

import apiClient from '@/api';
import { EnterpriseMailboxCard } from '@/components/email/EnterpriseMailboxCard';

// ===== UTILITY FUNCTIONS =====

function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ERROR BOUNDARY =====

class EmailAccountsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('EmailAccountsPage Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('EmailAccountsPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Something went wrong
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== INTERFACES =====

interface ApiEmailAccount {
  _id?: string;
  id?: string;
  email: string;
  provider: 'gmail' | 'outlook' | 'exchange' | 'imap';
  isActive: boolean;
  lastSyncAt?: string;
  syncStatus?: 'success' | 'error' | 'pending' | 'unknown';
  errorMessage?: string;
  totalEmails: number;
  unreadEmails: number;
  needsReauthentication?: boolean;
  addedAt?: string;
  tokenExpiresAt?: string;
  syncCount?: number;
  hasAccessToken?: boolean;
  hasRefreshToken?: boolean;
}

interface EmailAccountsApiResponse {
  emailAccounts?: ApiEmailAccount[];
  accounts?: ApiEmailAccount[];
  user?: {
    username: string;
    organization: string;
  };
  limits?: {
    maxAccounts: number;
    currentCount: number;
  };
}

// ===== EMAIL ACCOUNT CARD COMPONENT =====

interface EmailAccountCardProps {
  account: ApiEmailAccount;
  onToggleStatus: (email: string, isActive: boolean) => void;
  onRemove: (email: string) => void;
  onReauth: (provider: 'gmail' | 'outlook' | 'microsoft') => void;
  onSync: (email: string) => void;
  isSyncing: boolean;
}

const EmailAccountCard = memo<EmailAccountCardProps>(({
  account,
  onToggleStatus,
  onRemove,
  onReauth,
  onSync,
  isSyncing
}) => {
  const handleToggle = useCallback(() => {
    onToggleStatus(account.email, !account.isActive);
  }, [account.email, account.isActive, onToggleStatus]);

  const handleRemove = useCallback(() => {
    onRemove(account.email);
  }, [account.email, onRemove]);

  const handleSync = useCallback(() => {
    onSync(account.email);
  }, [account.email, onSync]);

  const handleReauth = useCallback(() => {
    onReauth(account.provider as 'gmail' | 'outlook' | 'microsoft');
  }, [account.provider, onReauth]);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'gmail':
        return '📧';
      case 'outlook':
        return '📬';
      case 'exchange':
        return '🏢';
      default:
        return '✉️';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{getProviderIcon(account.provider)}</span>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">
              {account.email}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
              {account.provider}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span 
            className={cn(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              account.isActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
            )}
          >
            {account.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {account.totalEmails || 0}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Total Emails
          </div>
        </div>
        <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {account.unreadEmails || 0}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Unread
          </div>
        </div>
      </div>

      {/* Status and Last Sync */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
          <span className={cn('text-sm font-medium', getStatusColor(account.syncStatus))}>
            {account.syncStatus || 'Unknown'}
          </span>
        </div>
        
        {account.lastSyncAt && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Last Sync:</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {formatDate(account.lastSyncAt)}
            </span>
          </div>
        )}

        {account.errorMessage && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
            {account.errorMessage}
          </div>
        )}

        {account.needsReauthentication && (
          <div className="text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
            Authentication required - please re-authorize this account
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {account.needsReauthentication && (
          <Button
            onClick={handleReauth}
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Re-authorize
          </Button>
        )}
        
        <Button
          onClick={handleSync}
          variant="outline"
          size="sm"
          disabled={isSyncing}
        >
          {isSyncing ? 'Syncing...' : 'Sync Now'}
        </Button>
        
        <Button
          onClick={handleToggle}
          variant="outline"
          size="sm"
          className={cn(
            !account.isActive
              ? 'text-green-600 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/20'
              : 'text-orange-600 border-orange-300 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-600 dark:hover:bg-orange-900/20'
          )}
        >
          {account.isActive ? 'Pause' : 'Activate'}
        </Button>
        
        <Button
          onClick={handleRemove}
          variant="outline"
          size="sm"
          className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
        >
          Remove
        </Button>
      </div>

      {/* Token Status */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-1">
            <span>Access Token:</span>
            <span className={account.hasAccessToken ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
              {account.hasAccessToken ? '✓' : '✗'}
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <span>Refresh Token:</span>
            <span className={account.hasRefreshToken ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
              {account.hasRefreshToken ? '✓' : '✗'}
            </span>
          </div>
          {account.addedAt && (
            <div className="col-span-2">
              Added: {formatDate(account.addedAt)}
            </div>
          )}
          {account.tokenExpiresAt && (
            <div className="col-span-2">
              Token Expires: {formatDate(account.tokenExpiresAt)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

EmailAccountCard.displayName = 'EmailAccountCard';

// ===== MAIN EMAIL ACCOUNTS PAGE COMPONENT =====
export const EmailAccountsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [syncingAccounts, setSyncingAccounts] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { getSafeUser } = useAuthStore();
  const { setGlobalError, clearGlobalError } = useAppStore();
  const { confirm } = useConfirmation();
  const currentUser = getSafeUser();

  // Debug logging to help troubleshoot user ID issues
  useEffect(() => {
    if (currentUser) {
    } else {
    }
  }, [currentUser]);

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Load email accounts
  const {
    data: emailAccountsResponse,
    loading: accountsLoading,
    error: accountsError,
    refetch: refreshAccounts
  } = useApi<EmailAccountsApiResponse>(
    async () => {

      if (!currentUser) {
        console.error('❌ [Email Accounts] No current user');
        throw new Error('User not authenticated');
      }

      // Extract user ID - handle both _id and id formats
      const userId = currentUser._id || currentUser.id;

      if (!userId) {
        console.error('❌ [Email Accounts] User ID not available', currentUser);
        throw new Error('User ID not available');
      }


      // Try to fetch email accounts from various possible endpoints
      let response;
      if (apiClient.users && 'getEmailAccounts' in apiClient.users) {
        response = await (apiClient.users as any).getEmailAccounts(userId);
      } else if (apiClient.auth && 'getEmailAccounts' in apiClient.auth) {
        response = await (apiClient.auth as any).getEmailAccounts(userId);
      } else if (apiClient.auth && 'me' in apiClient.auth) {
        // Fallback to user profile which should include email accounts
        const userProfile = await (apiClient.auth as any).me();
        response = userProfile?.emailAccounts ?
          { emailAccounts: userProfile.emailAccounts } : { emailAccounts: [] };
      } else {
        console.error('❌ [Email Accounts] No suitable API endpoint found');
        throw new Error('Email accounts API not available');
      }


      return response;
    },
    {
      immediate: !!currentUser && !!(currentUser._id || currentUser.id),
      rateLimitKey: 'email_accounts'
    }
  );

  // Error handling
  useEffect(() => {
    if (accountsError) {
      setGlobalError(`Email accounts loading error: ${accountsError}`);
    } else {
      clearGlobalError();
    }
  }, [accountsError, setGlobalError, clearGlobalError]);

  // Enhanced error handling on page load
useEffect(() => {

  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');
  const success = urlParams.get('success');
  const code = urlParams.get('code');
  const state = urlParams.get('state');


  // Check if we're returning from OAuth (has code and state)
  if (code && state) {

    // Check localStorage for stored OAuth state
    const storedState = localStorage.getItem('oauth_state');
    if (storedState) {
      try {
        const parsedState = JSON.parse(storedState);
      } catch (e) {
        console.error('❌ [OAuth Callback] Failed to parse stored state:', e);
      }
    } else {
      console.warn('⚠️ [OAuth Callback] No stored OAuth state found in localStorage');
    }
  }

  if (error) {
    console.error('❌ [OAuth Callback] Error in URL:', error);

    // Enhanced error messages based on error type
    let errorMessage = decodeURIComponent(error);

    // Provide specific guidance for refresh token issues
    if (errorMessage.includes('refresh token')) {
      errorMessage += '\n\nTo fix this:\n1. Go to Google Account settings\n2. Remove WorkiDoc from connected apps\n3. Try connecting again to grant fresh permissions';
    } else if (errorMessage.includes('offline access')) {
      errorMessage += '\n\nPlease ensure you grant offline access permission when prompted by Google.';
    }

    setGlobalError(errorMessage);

    // Clear error from URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('error');
    window.history.replaceState(null, '', newUrl.toString());
  }

  if (success) {

    // Clear any previous errors
    clearGlobalError();

    // Refresh email accounts data
    refreshAccounts();

    // Clear success from URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('success');
    window.history.replaceState(null, '', newUrl.toString());
  }

  if (!error && !success && !code) {
  }
}, [setGlobalError, clearGlobalError, refreshAccounts]);

  // Success message auto-clear
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Sync all emails mutation
  const syncAllEmailsMutation = useMutation<any, undefined>(
    async () => {
      if (apiClient.emails && 'syncAll' in apiClient.emails) {
        return await (apiClient.emails as any).syncAll();
      } else if (apiClient.emails && 'sync' in apiClient.emails) {
        return await (apiClient.emails as any).sync();
      } else {
        throw new Error('Email sync not available');
      }
    },
    {
      onSuccess: (result) => {
        setSuccessMessage('Email sync completed successfully');
        setSyncingAccounts([]);
        setTimeout(() => refreshAccounts(), 2000);
      },
      onError: (error: any) => {
        setGlobalError(`Sync failed: ${error.message || error}`);
        setSyncingAccounts([]);
      }
    }
  );

  // Sync individual account mutation
  const syncAccountMutation = useMutation<any, string>(
    async (email: string) => {
      if (apiClient.emails && 'syncAccount' in apiClient.emails) {
        return await (apiClient.emails as any).syncAccount(email);
      } else if (apiClient.emails && 'sync' in apiClient.emails) {
        return await (apiClient.emails as any).sync({ account: email });
      } else {
        throw new Error('Individual account sync not available');
      }
    },
    {
      onSuccess: (result) => {
        setSuccessMessage('Account sync completed successfully');
        setSyncingAccounts(prev => prev.filter(email => email !== result.email));
        setTimeout(() => refreshAccounts(), 2000);
      },
      onError: (error: any) => {
        setGlobalError(`Account sync failed: ${error.message || error}`);
        setSyncingAccounts([]);
      }
    }
  );

  // Remove account mutation
  const removeAccountMutation = useMutation<any, string>(
    async (email: string) => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Extract user ID - handle both _id and id formats
      const userId = currentUser._id || currentUser.id;
      
      if (!userId) {
        throw new Error('User ID not available');
      }

      
      if (apiClient.users && 'removeEmailAccount' in apiClient.users) {
        return await (apiClient.users as any).removeEmailAccount(userId, email);
      } else if (apiClient.auth && 'removeEmailAccount' in apiClient.auth) {
        return await (apiClient.auth as any).removeEmailAccount(userId, email);
      } else {
        throw new Error('Remove email account API not available');
      }
    },
    {
      onSuccess: () => {
        setSuccessMessage('Email account removed successfully');
        refreshAccounts();
      },
      onError: (error: any) => {
        setGlobalError(`Remove account failed: ${error.message || error}`);
      }
    }
  );

  // Toggle account status mutation
  const toggleAccountMutation = useMutation<any, { email: string; isActive: boolean }>(
    async ({ email, isActive }) => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Extract user ID - handle both _id and id formats
      const userId = currentUser._id || currentUser.id;
      
      if (!userId) {
        throw new Error('User ID not available');
      }

      
      if (apiClient.users && 'updateEmailAccountStatus' in apiClient.users) {
        return await (apiClient.users as any).updateEmailAccountStatus(userId, email, { isActive });
      } else if (apiClient.auth && 'updateEmailAccountStatus' in apiClient.auth) {
        return await (apiClient.auth as any).updateEmailAccountStatus(userId, email, { isActive });
      } else {
        console.warn('Account status toggle endpoint not available');
        return { success: true, message: 'Status toggle requested' };
      }
    },
    {
      onSuccess: () => {
        setSuccessMessage('Account status updated successfully');
        refreshAccounts();
      },
      onError: (error: any) => {
        setGlobalError(`Update account status failed: ${error.message || error}`);
      }
    }
  );

  // Extract email accounts
  const emailAccounts: ApiEmailAccount[] = useMemo(() => {
    const response = emailAccountsResponse;
    if (!response) return [];
    
    const accounts = response.emailAccounts || response.accounts || [];
    return Array.isArray(accounts) ? accounts : [];
  }, [emailAccountsResponse]);

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!debouncedSearchTerm) return emailAccounts;
    
    return emailAccounts.filter(account =>
      account.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      account.provider.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [emailAccounts, debouncedSearchTerm]);

  // Event handlers
  const handleAddGoogleAccount = useCallback(async () => {
  try {
    clearGlobalError();

    // Check if user already has email accounts
    const currentAccounts = emailAccounts.length;
    if (currentAccounts >= 5) {
      setGlobalError('Maximum 5 email accounts allowed per user');
      return;
    }

    // Start OAuth flow
    const authResponse: OAuth2AuthorizeResponse = await apiClient.auth.oauth2Authorize('gmail');

    if (!authResponse?.authorizationUrl) {
      throw new Error('No OAuth URL received from server');
    }

    // Store state for potential error handling
    localStorage.setItem('oauth_state', JSON.stringify({
      timestamp: Date.now(),
      returnUrl: window.location.href,
      action: 'add_gmail_account'
    }));

    // Redirect to Google OAuth
    window.location.href = authResponse.authorizationUrl;

  } catch (error: any) {
    console.error('OAuth initiation failed:', error);
    setGlobalError(error.message || 'Failed to initiate Google authentication');
  }
}, [clearGlobalError, setGlobalError, emailAccounts.length]);

  const handleAddMicrosoftAccount = useCallback(async () => {
  try {

    clearGlobalError();

    // Check if user already has email accounts
    const currentAccounts = emailAccounts.length;
    if (currentAccounts >= 5) {
      console.error('❌ [Microsoft OAuth] Account limit reached:', currentAccounts);
      setGlobalError('Maximum 5 email accounts allowed per user');
      return;
    }


    // Start Microsoft OAuth flow
    const authResponse: OAuth2AuthorizeResponse = await apiClient.auth.oauth2Authorize('microsoft');


    if (!authResponse?.authorizationUrl) {
      console.error('❌ [Microsoft OAuth] No authorization URL in response:', authResponse);
      throw new Error('No OAuth URL received from server');
    }

    // Store state for potential error handling
    const oauthState = {
      timestamp: Date.now(),
      returnUrl: window.location.href,
      action: 'add_microsoft_account'
    };

    localStorage.setItem('oauth_state', JSON.stringify(oauthState));


    // Redirect to Microsoft OAuth
    window.location.href = authResponse.authorizationUrl;

  } catch (error: any) {
    console.error('❌ [Microsoft OAuth] Initiation failed:', {
      message: error.message,
      stack: error.stack,
      response: error.response?.data
    });
    setGlobalError(error.message || 'Failed to initiate Microsoft authentication');
  }
}, [clearGlobalError, setGlobalError, emailAccounts.length]);

  const handleRemoveAccount = useCallback(async (email: string) => {
    const confirmed = await confirm({
      title: 'Remove Email Account',
      message: `Are you sure you want to remove ${email}? This will stop syncing emails from this account.`,
      confirmText: 'Remove',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await removeAccountMutation.mutate(email);
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [removeAccountMutation, confirm]);

  const handleReauth = useCallback(async (provider: 'gmail' | 'outlook' | 'microsoft' = 'gmail') => {
  try {
    clearGlobalError();

    // Show instructions for manual app revocation based on provider
    const confirmMessage = provider === 'gmail' || !provider ?
      `To ensure fresh tokens, please:

1. Go to Google Account settings (myaccount.google.com)
2. Navigate to "Security" → "Third-party apps with account access"
3. Find and remove "WorkiDoc" from the list
4. Click "Continue" below to reconnect with fresh permissions

This ensures you get a new refresh token.` :
      `To ensure fresh tokens, please:

1. Go to Microsoft Account settings (account.microsoft.com)
2. Navigate to "Privacy" → "Apps and services"
3. Find and remove "WorkiDoc" from the list
4. Click "Continue" below to reconnect with fresh permissions

This ensures you get a new access token.`;

    const confirmed = await confirm({
      title: 'Re-authorize Account',
      message: confirmMessage,
      confirmText: 'Continue',
      variant: 'warning',
    });
    if (!confirmed) {
      return;
    }

    const providerName = provider === 'outlook' || provider === 'microsoft' ? 'microsoft' : 'gmail';
    const authResponse: OAuth2AuthorizeResponse = await apiClient.auth.oauth2Authorize(providerName, true);
    if (authResponse?.authorizationUrl) {
      window.location.href = authResponse.authorizationUrl;
    } else {
      throw new Error('No OAuth URL received from server');
    }
  } catch (error: any) {
    setGlobalError(error.message || 'Failed to initiate re-authentication');
  }
}, [clearGlobalError, setGlobalError, confirm]);

  const handleToggleStatus = useCallback(async (email: string, isActive: boolean) => {
    try {
      await toggleAccountMutation.mutate({ email, isActive });
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [toggleAccountMutation]);

  const handleSyncAccount = useCallback(async (email: string) => {
    try {
      setSyncingAccounts(prev => [...prev, email]);
      await syncAccountMutation.mutate(email);
    } catch (error) {
      setSyncingAccounts(prev => prev.filter(e => e !== email));
    }
  }, [syncAccountMutation]);

  const handleSyncAllAccounts = useCallback(async () => {
    if (emailAccounts.length === 0) {
      setGlobalError('No email accounts to sync');
      return;
    }

    try {
      setSyncingAccounts(emailAccounts.map(account => account.email));
      await syncAllEmailsMutation.mutate(undefined);
    } catch (error) {
      setSyncingAccounts([]);
    }
  }, [emailAccounts, syncAllEmailsMutation, setGlobalError]);

  // Page actions
  const actions = useMemo(() => {
    const actionButtons = [];

    actionButtons.push(
      <Button
        key="add-google"
        onClick={handleAddGoogleAccount}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        Add Google Account
      </Button>
    );

    actionButtons.push(
      <Button
        key="add-microsoft"
        onClick={handleAddMicrosoftAccount}
        className="bg-blue-500 hover:bg-blue-600 text-white"
      >
        Add Microsoft Account
      </Button>
    );

    if (emailAccounts.length > 0) {
      actionButtons.push(
        <Button
          key="sync-all"
          onClick={handleSyncAllAccounts}
          variant="outline"
          disabled={syncingAccounts.length > 0}
        >
          {syncingAccounts.length > 0 ? 'Syncing...' : 'Sync All Accounts'}
        </Button>
      );
    }

    return actionButtons;
  }, [handleAddGoogleAccount, handleAddMicrosoftAccount, handleSyncAllAccounts, emailAccounts.length, syncingAccounts.length]);

  // Loading state
  if (accountsLoading) {
    return (
      <PageLayout title="Email Accounts" subtitle="Manage your connected email accounts">
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  return (
    <EmailAccountsErrorBoundary>
      <PageLayout 
        title="Email Accounts" 
        subtitle="Manage your connected email accounts"
        actions={actions}
      >
        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-green-800 dark:text-green-200">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Org-managed mailbox enrollment (renders only if the org has
            Microsoft 365 in Application Permissions mode). */}
        <EnterpriseMailboxCard onAdded={() => refreshAccounts?.()} />

        {/* Search and Filters */}
        {emailAccounts.length > 0 && (
          <div className="mb-6">
            <div className="max-w-md">
              <Input
                type="text"
                placeholder="Search email accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Email Accounts Grid */}
        {filteredAccounts.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredAccounts.map((account) => (
              <EmailAccountCard
                key={account.email}
                account={account}
                onToggleStatus={handleToggleStatus}
                onRemove={handleRemoveAccount}
                onReauth={handleReauth}
                onSync={handleSyncAccount}
                isSyncing={syncingAccounts.includes(account.email)}
              />
            ))}
          </div>
        ) : emailAccounts.length > 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No accounts found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No email accounts match your search criteria.
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No email accounts
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first email account.
            </p>
          </div>
        )}
      </PageLayout>
    </EmailAccountsErrorBoundary>
  );
};